import { useEffect, useRef } from 'react'
import { auth } from '@/config/firebase'
import { API_CONFIG } from '@/config/api'
import type { InterviewMessage } from '@/types/api'

type AgentState = 'thinking' | 'extracting' | 'idle'

interface InterviewStreamHandlers {
  sessionId?: string | null
  enabled?: boolean
  onMessage?: (message: InterviewMessage) => void
  onMessageAck?: (payload: { clientId: string; messageId: string }) => void
  onAgentState?: (state: AgentState) => void
  onContextPatch?: (payload: any) => void
  onGapUpdate?: (payload: any) => void
  onError?: (error: Event) => void
}

const getAuthToken = async () => {
  if (!auth?.currentUser) {
    if (import.meta.env.DEV) {
      return 'MOCK_DEV_TOKEN'
    }
    return null
  }

  try {
    return await auth.currentUser.getIdToken()
  } catch (error) {
    console.error('Failed to get auth token for SSE:', error)
    return null
  }
}

export const useInterviewStream = ({
  sessionId,
  enabled = true,
  onMessage,
  onMessageAck,
  onAgentState,
  onContextPatch,
  onGapUpdate,
  onError
}: InterviewStreamHandlers) => {
  const sourceRef = useRef<EventSource | null>(null)

  useEffect(() => {
    if (!sessionId || !enabled) return

    let isActive = true

    const connect = async () => {
      const token = await getAuthToken()
      if (!isActive) return

      const baseUrl = API_CONFIG.BASE_URL
      const url = new URL(`${baseUrl}/v1/streams/interviews/${sessionId}`)
      if (token) {
        url.searchParams.set('token', token)
      }

      const source = new EventSource(url.toString())
      sourceRef.current = source

      source.addEventListener('message', (event) => {
        try {
          const payload = JSON.parse(event.data)
          if (payload?.message) {
            onMessage?.(payload.message)
          }
        } catch (error) {
          console.error('Failed to parse SSE message event:', error)
        }
      })

      source.addEventListener('message.ack', (event) => {
        try {
          const payload = JSON.parse(event.data)
          if (payload?.clientId && payload?.messageId) {
            onMessageAck?.(payload)
          }
        } catch (error) {
          console.error('Failed to parse SSE message.ack event:', error)
        }
      })

      source.addEventListener('agent.state', (event) => {
        try {
          const payload = JSON.parse(event.data)
          if (payload?.state) {
            onAgentState?.(payload.state)
          }
        } catch (error) {
          console.error('Failed to parse SSE agent.state event:', error)
        }
      })

      source.addEventListener('context.patch', (event) => {
        try {
          const payload = JSON.parse(event.data)
          onContextPatch?.(payload)
        } catch (error) {
          console.error('Failed to parse SSE context.patch event:', error)
        }
      })

      source.addEventListener('gap.update', (event) => {
        try {
          const payload = JSON.parse(event.data)
          onGapUpdate?.(payload)
        } catch (error) {
          console.error('Failed to parse SSE gap.update event:', error)
        }
      })

      source.onerror = (event) => {
        onError?.(event)
      }
    }

    connect()

    return () => {
      isActive = false
      if (sourceRef.current) {
        sourceRef.current.close()
        sourceRef.current = null
      }
    }
  }, [sessionId, enabled, onMessage, onMessageAck, onAgentState, onContextPatch, onGapUpdate, onError])
}
